import { useCallback, useEffect, useState, type ReactNode } from 'react';
import { Backdrop, HeroBackdrop } from '../../../modules/design-system';
import {
  getOrderIdFromURL,
  getViewFromURL,
  setGameIdInURL,
  setOrderIdInURL,
  setViewInURL,
  useSession,
} from '../../../modules/session';
import { useAuth } from '../../../modules/auth/useAuth';
import { useGamePurchase } from '../../../modules/game/LootBoxOpening';
import { RevealScreen } from '../../../modules/reveal';
import { useBackHandler } from '../../../modules/backStack';
import { DemoRevealOverlay, useDemoEmbedKey } from '../../../modules/demo';
import { MyPacksPage } from '../MyPacks';
import { pushMyPacksRoute } from '../../../pages/MyPacks/myPacksRoute';
import type {
  ApiClient,
  AvailableCategory,
  IframePoolOrderItem,
} from '../../../modules/api';
import { useResolvedCategory } from '../../../modules/pack';
import { LoadingScreen } from '../../../modules/shell';
import { Header, type HeaderCategory } from '../Header';
import { useTrackViewItem } from '../../../modules/tracking';
import { usePackData } from './usePackData';
import { useCategoryOffers } from './useCategoryOffers';
import { usePackVolatility } from './usePackVolatility';
import { useVaultCardCount } from './useVaultCardCount';
import { useDepositToRip } from './useDepositToRip';
import { PackLayout } from './PackLayout';
import { WhatsInsidePage } from '../WhatsInside';
import { PurchaseOverlay } from './PurchaseOverlay';

export interface PackPageProps {
  client: ApiClient;
  /** game_id from the URL, or null on first load. */
  initialGameId: string | null;
}

// Text-only on purpose: Betr's category switcher has no icons (partner
// Figma 2026-10-08), on the mobile dropdown and the desktop pill row alike.
function toHeaderCategories(cats: AvailableCategory[]): HeaderCategory[] {
  return cats.map((c) => ({ id: String(c.id), label: c.label }));
}

/**
 * Modern Pack page.
 *
 * Two-tier active state:
 *
 *   • `urlGameId`     — what's in the URL. Driven by header pill clicks;
 *                       resolves which sport's pack list is loaded.
 *   • `activeOfferId` — what's centred in the slider. Driven by swipes
 *                       too; the rest of the page (info card, odds, buy
 *                       price) follows this value. URL is **not** updated
 *                       on swipe — the slider browses inside a category.
 *
 * Switching category in the header resets both: URL changes to the
 * category's representative pool_offer_id and the slider snaps to it.
 */
export function PackPage({
  client,
  initialGameId,
}: PackPageProps) {
  const [urlGameId, setUrlGameId] = useState<string | null>(initialGameId);
  const [activeOfferId, setActiveOfferId] = useState<string | null>(initialGameId);
  const [quantity, setQuantity] = useState(1);
  const [volatility, setVolatility] = usePackVolatility(activeOfferId);
  // Mirror of the URL's `?order_id=…` — when set, <RevealScreen /> owns
  // the viewport (mirrors classic). Refresh-safe: state is seeded from
  // the URL on mount, so a hard reload during ripping resumes there.
  const [revealOrderId, setRevealOrderId] = useState<string | null>(() =>
    getOrderIdFromURL(),
  );

  // Sub-views inside Pack: `'pack'` (default), `'my-packs'` (header button)
  // and `'whats-inside'` (the pack pool, lobby pill). Both written to and read
  // from `?view=…` so refresh keeps you on the same screen. Sleek has no Just
  // Pulled (2026-10-09): a stale `?view=just-pulled` lands on the lobby.
  const [view, setView] = useState<'pack' | 'my-packs' | 'whats-inside'>(() => {
    const fromUrl = getViewFromURL();
    if (fromUrl === 'my-packs') return 'my-packs';
    if (fromUrl === 'whats-inside') return 'whats-inside';
    return 'pack';
  });

  // The Vault was this load's first screen: an app tab opened `/my-packs`
  // straight away. Cleared for good once the player leaves it, so a Vault
  // reached from the lobby keeps its Home button.
  const [isVaultEntry, setIsVaultEntry] = useState(view === 'my-packs');
  if (isVaultEntry && view !== 'my-packs') setIsVaultEntry(false);

  // Items handed from the reveal "Confirm Shipping" CTA into the My Packs
  // delivery flow. Seeds the create-shipping page; cleared when My Packs closes.
  const [deliverySeed, setDeliverySeed] = useState<IframePoolOrderItem[] | null>(null);

  const { token, getToken, embedKey } = useSession();
  const designSystemDemoKey = useDemoEmbedKey();
  const { requestAuth } = useAuth();

  // Free demo rip overlay toggle — see `canShowDemo` below (defined once
  // `offer` is loaded) for the gating rule.
  // Seeded from the path so `/demo/:offer_id` opens the ceremony straight
  // away — that link is meant to be sent to someone with no account.
  const [demoOpen, setDemoOpen] = useState(() => getViewFromURL() === 'demo');

  // Header "My Packs" — anonymous players (embed mode, no token) sign in
  // first; navigation proceeds only after a successful auth. A declined
  // modal stays on the pack page.
  const goMyPacks = useCallback(() => {
    if (!getToken()) {
      void requestAuth().then((ok) => {
        if (ok) setView('my-packs');
      });
      return;
    }
    setView('my-packs');
  }, [getToken, requestAuth]);

  // Belt for the same gate: My Packs is a personal view, so eject to the
  // pack view whenever there is no session — covers an anonymous boot with
  // a stale `?view=my-packs` URL and an embed 401 that dropped the token
  // while My Packs was open.
  useEffect(() => {
    if (!token && view === 'my-packs') setView('pack');
  }, [token, view]);

  useEffect(() => {
    if (revealOrderId === null) setOrderIdInURL(null);
  }, [revealOrderId]);

  // Once My Packs has mounted with the reveal ship-seed (it copies it into a
  // one-shot of its own at mount), drop our copy — otherwise a later My Packs
  // remount would re-capture the stale seed and pre-load a Cards-tab delivery
  // with the already-shipped card.
  useEffect(() => {
    if (deliverySeed && view === 'my-packs') setDeliverySeed(null);
  }, [deliverySeed, view]);

  useEffect(() => {
    // While the demo is up it owns the path (`/demo/:offer_id`); without
    // this guard the mount-time sync would wipe a cold demo link back to `/`.
    if (demoOpen) return;
    setViewInURL(view === 'pack' ? null : view);
  }, [view, demoOpen]);

  // Host back button (modules/backStack) leaves a sub-view the same way its
  // own Back button does.
  useBackHandler(
    view !== 'pack',
    () => {
      setDeliverySeed(null);
      setView('pack');
    },
    'view',
  );


  // Detail / odds / hits / categories for the centred offer.
  const {
    offer,
    categories,
    odds,
    maxForPurchaseCount,
    bgImageUrl,
    potentialHits,
    potentialHitsLoading,
    leavingForLab,
  } = usePackData(client, activeOfferId);
  useTrackViewItem(offer);

  // Free demo rip — gated on the pack's own `demoMode` AND an embed_key to
  // resolve the operator with on the anonymous demo endpoint. The launch URL
  // carries one only in embed mode, so a session-token launch falls back to
  // the key design_system hands over (sent only when a demo pack exists).
  const demoKey = embedKey ?? designSystemDemoKey;
  const canShowDemo = Boolean(offer?.demoMode) && demoKey !== null;

  // `/demo/:offer_id` is the screen's own address: mirror the open overlay
  // into the path (so a player can hand the link over) and, when a cold link
  // turns out to be dead — demo switched off, no embed key, unknown pack —
  // drop back to the pack page instead of holding a URL that shows nothing.
  useEffect(() => {
    if (!demoOpen) return;
    if (!offer) return;
    if (!canShowDemo) {
      setDemoOpen(false);
      return;
    }
    setViewInURL('demo', String(offer.id));
  }, [demoOpen, offer, canShowDemo]);

  // First-load fallback — seed both ids from the first available category
  // when the host didn't pass a `game_id`.
  useEffect(() => {
    if (urlGameId !== null) return;
    if (categories.length === 0) return;
    const first = categories[0];
    if (!first) return;
    const id = String(first.id);
    setUrlGameId(id);
    setActiveOfferId(id);
  }, [urlGameId, categories]);

  // The "active category" is the one that actually contains the URL's pack —
  // it drives the highlighted sport pill and the offers that populate the
  // slider. A deep link can point at any pack in the category, not just the
  // representative one, so resolve it by scanning rather than matching the
  // representative id directly (see useResolvedCategory).
  const urlCategory = useResolvedCategory(client, categories, urlGameId);

  const { offers: categoryOffers } = useCategoryOffers(client, urlCategory);

  // Purchase orchestration — `useGamePurchase` owns the wallet refresh,
  // the buy→reveal flow, the API wrapper, and the Just-Pulled quick-buy
  // queue. The slider can swap the visible pack without touching the
  // URL, so we hand the hook `activeOfferId` (centred offer) with a
  // `urlGameId` fallback. Shared with the classic Pack page.
  const { buy, flow, resetFlow } = useGamePurchase({
    client,
    offerId: activeOfferId ?? urlGameId ?? '',
    offer,
    quantity,
    setQuantity,
    volatility,
    onRevealReady: (orderId) => {
      setOrderIdInURL(orderId);
      setRevealOrderId(orderId);
    },
  });
  const { isShortOfFunds, onBuy } = useDepositToRip(offer, quantity, buy);

  // `activeOfferId` is owned by explicit user intent — header pill click
  // and slider swipe each set it directly. Don't auto-snap here on
  // `categoryOffers` change: during a category switch the offers list is
  // briefly stale (still old sport), and any snap-to-first-in-list logic
  // races with the freshly-set representative and overwrites it. The
  // Slider's own `externalIndex` falls back to 0 visually when the
  // active id isn't (yet) in the list — that's the correct intermediate
  // state until the new offers resolve.

  // Re-checked whenever a signed-in player lands back on the pack view.
  const vaultCardCount = useVaultCardCount(
    client,
    token !== null && view === 'pack' && revealOrderId === null,
  );

  const headerCategories = toHeaderCategories(categories);
  // Highlight the pill of the *resolved* category, so a deep link to a
  // non-representative pack still lights up its sport (the representative id
  // won't equal the URL's game_id in that case).
  const activeCategoryId =
    urlCategory !== null ? String(urlCategory.id) : undefined;

  // Post-purchase reveal — when `?order_id=X` is present the reveal screen
  // owns the viewport (matches classic). Refresh-safe: state is mirrored
  // from the URL on mount, so a hard reload while ripping resumes here.
  let content: ReactNode = null;
  if (revealOrderId) {
    content = (
      <RevealScreen
        client={client}
        orderId={revealOrderId}
        volatility={volatility}
        onClose={() => setRevealOrderId(null)}
        onBuyAgain={() => buy()}
        onShowPack={() => setRevealOrderId(null)}
        onShip={(item) => {
          // Confirm Shipping → drop into My Packs' create-delivery flow,
          // seeded with this card. Open the route first so My Packs mounts
          // straight into delivery-new.
          setDeliverySeed([item]);
          pushMyPacksRoute({ kind: 'delivery-new' });
          setRevealOrderId(null);
          setView('my-packs');
        }}
      />
    );
  }

  // A custom pack is played in the lab, which the page is already on its way to: none of this page shows meanwhile.
  if (leavingForLab) return <LoadingScreen />;

  return (
    <>
      {content}

      {demoOpen && offer && revealOrderId === null && (
        <DemoRevealOverlay
          client={client}
          offer={offer}
          embedKey={demoKey}
          onClose={() => setDemoOpen(false)}
        />
      )}

      {/* Pack view scope — wraps Backdrop AND the content div so the
          `[data-page='pack']` CSS-var redefinition in index.css reaches
          the gradient layer. Backdrop was previously a sibling of the
          wrapper, which left it reading root-level `--ui-bg-gradient`
          and ignoring page-specific knobs.

          Hidden while reveal is active so the page underneath doesn't
          flash during refresh-safe restore. */}
      <div data-page="pack" className={revealOrderId ? 'hidden' : ''}>
        {/* What's inside is a flat page (partner Figma 2026-10-06) — no pack photo. */}
        <Backdrop bgImageUrl={view === 'whats-inside' ? null : bgImageUrl} />
      </div>

      {!revealOrderId && view === 'whats-inside' && (
        <WhatsInsidePage
          items={potentialHits}
          loading={potentialHitsLoading}
          onBack={() => setView('pack')}
        />
      )}

      {!revealOrderId && view === 'my-packs' && (
        <MyPacksPage
          client={client}
          {...(deliverySeed ? { deliverySeed } : {})}
          isEntryScreen={isVaultEntry}
          onBack={() => {
            setDeliverySeed(null);
            setView('pack');
          }}
        />
      )}

      {/* Conditionally render — not just `hidden` — because the
          BottomAction `MobileBar` inside PackLayout portals to <body>
          and would leak through `display: none` (the portal escapes the
          parent's class). Unmounting on view-change drops the portal too. */}
      {!revealOrderId && view === 'pack' && (
      <div
        data-co-component="PackPageBackground"
        // The screen minus the host insets `#root` already pads with: a plain
        // `min-h-screen` on top of that padding overflowed by the insets and
        // made the lobby scroll inside the partner's app (feedback 2026-10-07).
        className="@container relative flex min-h-[calc(100dvh_-_var(--page-safe-top)_-_var(--page-safe-bottom))] w-full flex-col"
        style={{ color: 'var(--color-text-on-brand)' }}
      >
        {/* Sonar-waves backdrop — spans header + slider area so the
            rings bleed *under* the navigation bar at the top and
            *past* the pack at the bottom, matching Figma 543:2118.
            Horizontally constrained on desktop to the slider column
            only (stops before the sidebar) so the wave's centre lines
            up with the centre pack — otherwise the rings would
            visually drift right toward viewport centre.
            Perf:
              • `scale(1.5)` instead of `scale(2)` — paint area is the
                square of scale, so 1.5² ≈ 56% of the work 2² did, with
                still enough bleed to fill the slot.
              • `[will-change:transform]` + `[transform:translateZ(0)]`
                lift the inner wrapper onto its own GPU compositing
                layer so Lottie's per-frame repaints don't reflow the
                surrounding page. */}
        <div
          aria-hidden
          className="
            pointer-events-none absolute left-0 right-0 top-0 -z-10 overflow-hidden
            h-[560px]
            @[600px]:h-[640px]
            @[800px]:right-[384px] @[800px]:h-[700px]
            @[1200px]:right-[424px]
            [contain:layout_paint]
          "
          // Off by default in sleek — the partner wants a flat page (Loom
          // 2026-10-06); an operator can still switch the rings back on.
          style={{ display: 'var(--co-PackPageBackground-sonarDisplay, none)' }}
        >
          <div
            className="
              absolute inset-0 origin-center
              [transform:translateZ(0)_scale(1.5)]
              [will-change:transform]
              [backface-visibility:hidden]
            "
          >
            <HeroBackdrop
              animationUrl={offer?.heroAnimationUrl ?? null}
              renderer="canvas"
            />
          </div>
        </div>

        <Header
          categories={headerCategories}
          {...(activeCategoryId ? { activeCategoryId } : {})}
          onCategorySelect={(id) => {
            // Header pill — switch category. URL + slider both snap to
            // the new category's representative offer.
            setGameIdInURL(id);
            setUrlGameId(id);
            setActiveOfferId(id);
          }}
          onMyPacksClick={goMyPacks}
          vaultCardCount={vaultCardCount}
        />

        <PackLayout
          offer={offer}
          categoryOffers={categoryOffers}
          activeOfferId={activeOfferId !== null ? Number(activeOfferId) : null}
          odds={odds}
          maxForPurchaseCount={maxForPurchaseCount}
          // Kept while a swiped-to pack's pool loads — the page shows its
          // skeleton meanwhile; dropping it flashed the pill on every swipe
          // (partner feedback 2026-10-16). Gone only for a pack with no pool.
          {...(potentialHitsLoading || potentialHits.length > 0 ? { onShowWhatsInside: () => setView('whats-inside') } : {})}
          categories={headerCategories}
          {...(activeCategoryId ? { activeCategoryId } : {})}
          onCategorySelect={(id) => {
            setGameIdInURL(id);
            setUrlGameId(id);
            setActiveOfferId(id);
          }}
          onSlideChange={(offerId) => {
            // Slider swipe — update centred offer in state only, keep URL.
            setActiveOfferId(String(offerId));
          }}
          onTierSelect={(poolOfferId) => {
            const id = String(poolOfferId);
            setGameIdInURL(id);
            setUrlGameId(id);
            setActiveOfferId(id);
          }}
          quantity={quantity}
          onQuantityChange={setQuantity}
          onBuy={onBuy}
          isShortOfFunds={isShortOfFunds}
          unitPrice={offer?.price ?? 0}
          volatility={volatility}
          onVolatilityChange={setVolatility}
          showDemo={canShowDemo}
          onOpenDemo={() => setDemoOpen(true)}
        />
      </div>
      )}

      {/* Global purchase overlay — shows the spinner during the API
          round-trip and the failure screen on errors. Success closes
          the overlay and hands off to <RevealScreen> via the URL. */}
      <PurchaseOverlay state={flow} onClose={resetFlow} />
    </>
  );
}
